import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTable } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { Flex } from "@redotech/redo-web/flex";
import { ProductListItem } from "@redotech/redo-web/product-list-item";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useDeviceLocation } from "../hooks/use-device-location";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import * as styles from "./bin-assignment-modal.module.css";

interface ItemWithBinAssignment extends FulfillmentOrderLineItem {
  binValue: string;
  hasVariantId: boolean;
  hasProductId: boolean;
  canAssignBin: boolean;
}

export const BinAssignmentModal = memo(function BinAssignmentModal({
  groups,
  open,
  setOpen,
}: {
  groups: readonly FulfillmentGroupData[];
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const [deviceLocationId] = useDeviceLocation();

  const variantIdsToCheck = useMemo(() => {
    if (!open) {
      return [];
    }

    const identifiers = new Set<string>();

    for (const group of groups) {
      for (const lineItem of group.items) {
        const hasExistingBin = group.lineItemLocations?.some(
          (location) =>
            location.sku === lineItem.sku &&
            location.bins?.some((bin) => bin.bin),
        );

        if (!hasExistingBin && lineItem.variantId) {
          identifiers.add(lineItem.variantId);
        }
      }
    }

    return Array.from(identifiers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Use RPC to check which products actually need bins
  const productsWithoutBinsCheck = useLoad(() => {
    if (variantIdsToCheck.length === 0 || !open) {
      return Promise.resolve({ identifiers: [] });
    }
    return outboundLabelsRpcClient.getProductsWithoutBinMetafield({
      identifiers: variantIdsToCheck,
    });
  }, [outboundLabelsRpcClient, variantIdsToCheck, open]);

  // Extract items without bins from selected groups
  const itemsNeedingBins = useMemo(() => {
    const items: ItemWithBinAssignment[] = [];
    const seenItems = new Set<string>();
    const identifiersNeedingBins = new Set(
      productsWithoutBinsCheck.value?.identifiers || [],
    );

    for (const group of groups) {
      for (const lineItem of group.items) {
        const hasVariantId = !!lineItem.variantId;
        const hasProductId = !!lineItem.productId;

        // Check if this item already has bin data from location info
        const hasExistingBin = group.lineItemLocations?.some(
          (location) =>
            location.sku === lineItem.sku &&
            location.bins?.some((bin) => bin.bin),
        );

        // Check if this item needs a bin based on RPC response or has no variantId
        const needsBinFromRpc =
          hasVariantId &&
          lineItem.variantId &&
          identifiersNeedingBins.has(lineItem.variantId);
        const shouldInclude =
          needsBinFromRpc || (!hasVariantId && !hasExistingBin);
        const canAssignBin = Boolean(
          hasVariantId && hasProductId && needsBinFromRpc,
        );

        // Create unique key to avoid duplicates across groups
        const itemKey = `${lineItem.sku}-${lineItem.variantId}-${lineItem.productId}`;

        if (!hasExistingBin && !seenItems.has(itemKey) && shouldInclude) {
          seenItems.add(itemKey);
          items.push({
            ...lineItem,
            binValue: "",
            hasVariantId,
            hasProductId,
            canAssignBin,
          });
        }
      }
    }

    return items.sort((a, b) => {
      // Sort items that can be assigned bins first
      if (a.canAssignBin && !b.canAssignBin) {
        return -1;
      }
      if (!a.canAssignBin && b.canAssignBin) {
        return 1;
      }

      // Within the same assignability, put items with variantIds first
      if (a.hasVariantId && !b.hasVariantId) {
        return -1;
      }
      if (!a.hasVariantId && b.hasVariantId) {
        return 1;
      }

      return (a.sku || "").localeCompare(b.sku || "");
    });
  }, [groups, productsWithoutBinsCheck.value]);

  const [itemBinValues, setItemBinValues] = useState<Record<string, string>>(
    {},
  );

  const updateBinValue = useHandler((itemKey: string, value: string) => {
    setItemBinValues((prev) => ({ ...prev, [itemKey]: value }));
  });

  const [assignBinsLoad, triggerAssignBins] = useTriggerLoad(async () => {
    const promises = itemsNeedingBins
      .filter((item) => {
        if (!item.canAssignBin || !item.variantId) {
          return false;
        }
        const itemKey = `${item.sku}-${item.variantId}-${item.productId}`;
        const binValue = itemBinValues[itemKey];
        return binValue && binValue.trim(); // Only include items with actual bin values
      })
      .map(async (item) => {
        const itemKey = `${item.sku}-${item.variantId}-${item.productId}`;
        const binValue = itemBinValues[itemKey]?.trim() ?? "";

        try {
          const result =
            await outboundLabelsRpcClient.updateVariantBinMetafield({
              variantId: item.variantId!,
              binValue,
              locationId: deviceLocationId ?? null,
            });

          if (!result.success) {
            console.error(
              `Failed to update bin for ${item.sku}:`,
              result.errors,
            );
            return { success: false, sku: item.sku, errors: result.errors };
          }

          return {
            success: true,
            sku: item.sku,
            binCreated: result.binCreated ?? false,
            binValue,
          };
        } catch (error) {
          console.error(`Error updating bin for ${item.sku}:`, error);
          return {
            success: false,
            sku: item.sku,
            errors: [error instanceof Error ? error.message : String(error)],
          };
        }
      });

    const results = await Promise.all(promises);
    const failures = results.filter((r) => !r.success);
    const successes = results.filter((r) => r.success);

    if (failures.length > 0) {
      const failedSkus = failures.map((f) => f.sku).join(", ");
      toast(`Failed to update bins for: ${failedSkus}`, { variant: "error" });
    }

    if (successes.length > 0) {
      toast(`Successfully updated ${successes.length} bin assignment(s)`, {
        variant: "success",
      });
    }

    const createdBins = successes
      .map((s) => ("binCreated" in s && s.binCreated ? s.binValue : null))
      .filter((v): v is string => !!v);
    if (createdBins.length > 0) {
      toast(
        `Created new bin${createdBins.length === 1 ? "" : "s"}: ${createdBins.join(", ")}`,
        { variant: "warning" },
      );
    }

    if (failures.length === 0) {
      setOpen(false);
    }
  });

  const canSubmit = Object.values(itemBinValues).some(
    (value) => value.trim().length > 0,
  );

  return (
    <RedoBaseModal
      isOpen={open}
      onModalCloseRequested={() => setOpen(false)}
      otherClasses={[styles.modal]}
    >
      <RedoModalHeader
        cancelClicked={() => setOpen(false)}
        subtitle="Assign bin locations to items that can be updated in Shopify"
        title="Assign bin locations"
      />

      <Flex dir="column" gap="lg">
        {productsWithoutBinsCheck.pending ||
        !productsWithoutBinsCheck?.value ? (
          <Flex p="3xl">
            <Text fontSize="md" textColor="secondary">
              Loading items that need bin assignments...
            </Text>
          </Flex>
        ) : itemsNeedingBins.length === 0 ? (
          <Flex p="3xl">
            <Text fontSize="md" textColor="secondary">
              No items need bin assignments. All items either have bins assigned
              or don't exist in Shopify.
            </Text>
          </Flex>
        ) : (
          <RedoTable<ItemWithBinAssignment>
            additionalClasses={styles.table}
            layout={{
              columns: [
                {
                  widthMode: { type: "fixed", width: 200 },
                  renderHeaderCell: () => (
                    <Flex pl="lg">
                      <Text fontSize="sm" fontWeight="medium">
                        SKU
                      </Text>
                    </Flex>
                  ),
                  renderNormalCell: ({ row }) => (
                    <Flex pl="lg">
                      <Text fontSize="sm" fontWeight="medium">
                        {row.sku || "No SKU"}
                      </Text>
                    </Flex>
                  ),
                },
                {
                  widthMode: { type: "fill" },
                  renderHeaderCell: () => (
                    <Flex pl="lg">
                      <Text fontSize="sm" fontWeight="medium">
                        Product
                      </Text>
                    </Flex>
                  ),
                  renderNormalCell: ({ row }) => {
                    return (
                      <Flex pl="lg">
                        <ProductListItem
                          disableTooltip={false}
                          imageUrl={row.imageUrl}
                          sku={row.sku}
                          title={row.productTitle || row.title}
                          variantTitle={row.variantTitle}
                        />
                      </Flex>
                    );
                  },
                },
                {
                  widthMode: { type: "fixed", width: 140 },
                  renderHeaderCell: () => (
                    <Flex pl="lg">
                      <Text fontSize="sm" fontWeight="medium">
                        Status
                      </Text>
                    </Flex>
                  ),
                  renderNormalCell: ({ row }) => (
                    <Flex pl="lg">
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        textColor={row.canAssignBin ? "success" : "warning"}
                      >
                        {row.canAssignBin
                          ? "Can update"
                          : row.hasVariantId
                            ? "Bad product mapping"
                            : "Bad product mapping"}
                      </Text>
                    </Flex>
                  ),
                },
                {
                  widthMode: { type: "fixed", width: 200 },
                  renderHeaderCell: () => (
                    <Flex pl="lg">
                      <Text fontSize="sm" fontWeight="medium">
                        Bin Location
                      </Text>
                    </Flex>
                  ),
                  renderNormalCell: ({ row }) => {
                    const itemKey = `${row.sku}-${row.variantId}-${row.productId}`;
                    if (!row.canAssignBin) {
                      return (
                        <Flex pl="lg">
                          <Text fontSize="sm" textColor="secondary">
                            N/A
                          </Text>
                        </Flex>
                      );
                    }

                    return (
                      <Flex pl="lg">
                        <RedoTextInput
                          placeholder="Enter bin location"
                          setValue={(value: string) =>
                            updateBinValue(itemKey, value)
                          }
                          size="sm"
                          value={itemBinValues[itemKey] || ""}
                        />
                      </Flex>
                    );
                  },
                },
              ],
            }}
            rowsOfData={itemsNeedingBins}
            size="md"
          />
        )}
      </Flex>

      <Flex
        align="center"
        dir="row"
        gap="md"
        justify="flex-end"
        p="lg"
        w="full"
      >
        <RedoButton
          hierarchy="secondary"
          onClick={() => setOpen(false)}
          size="md"
          text="Cancel"
          theme="normal"
        />
        <RedoButton
          disabled={!canSubmit || itemsNeedingBins.length === 0}
          hierarchy="primary"
          onClick={() => triggerAssignBins()}
          pending={assignBinsLoad.pending}
          size="md"
          text="Assign bins"
          theme="normal"
        />
      </Flex>
    </RedoBaseModal>
  );
});
